import { cloneDeep } from 'lodash';
import React, { useMemo, useState } from 'react';
import { ProductionInput, Reference, ResourceInput } from 'sr-types/lib/production/v1/graphql';
import Stepper, { StepsIndexConstant } from '../common/components/Stepper';
import { useStepper } from '../common/components/StepperContext';
import FormModal from '../common/form/FormModal';
import { useL10n } from '../common/hooks/useL10n';
import { useI18n } from '../common/i18n/I18n';
import CreateProductionForm from '../production/form/CreateProductionForm';
import { productionFormInput } from '../production/helpers/productionApi';
import { ProductionWithReference, removeOwnerOrganizationAndSetId } from '../production/helpers/productionUtils';
import { useCreateProductionResourceShortlist } from '../production/stepperForms/stepform';
import { RESOURCE_TYPE } from '../resource/common/resourceConstants';
import ResourceTypeForm from '../resource/stepper/ResourceTypeForm';
import { ORGANIZATION_TYPE, PEOPLE_TYPE } from '../shortlists/form/inquiry/inquiry';
import CreateInvitePreviewStep from './CreateInvitePreviewStep';
import CreateRequestAvails from './CreateRequestAvails';
import CreateResourceInStepper from './CreateResourceInStepper';
import { handleEntitiesToProductionMutations } from './hireResource';

type HireResourceStepperType = {
    resType: string;
    onCancelStepper: () => void;
};

export default (props: HireResourceStepperType) => {
    const { resType, onCancelStepper } = props;
    const { saveProductionFromStepperTemplate } = useCreateProductionResourceShortlist();
    const { associateEntitiesWithStepperProduction } = handleEntitiesToProductionMutations();
    const [isExpandedModal, setIsExpandedModal] = useState(false);
    const { activeStep } = useStepper();
    const { l10n } = useL10n();

    const onSave = (data: any) => {
        let resDataStep = StepsIndexConstant.zero;
        let prodDataStep = StepsIndexConstant.one;
        let inquiryDataStep = StepsIndexConstant.two;

        if (resType === RESOURCE_TYPE.supplier) {
            resDataStep = StepsIndexConstant.one;
            prodDataStep = StepsIndexConstant.two;
            inquiryDataStep = StepsIndexConstant.three;
        }
        const resData: ResourceInput = data[resDataStep];
        const prodData = data[prodDataStep];
        const inquiryData = data[inquiryDataStep];
        const productionWithOwnerOrganizationId = removeOwnerOrganizationAndSetId(
            cloneDeep(prodData)
        ) as ProductionWithReference;
        const prodInputData = productionFormInput(productionWithOwnerOrganizationId, l10n) as ProductionInput;

        // Create Production(if new), its associated Resource and resource associated shortlist, shortlist lineitems, and send inquiry
        return new Promise<void>(() => {
            saveProductionFromStepperTemplate(prodInputData).then(
                (productionData: { id: string; reference: Reference }) => {
                    inquiryData.production.reference = productionData.reference;
                    associateEntitiesWithStepperProduction({
                        productionId: productionData.id,
                        resData,
                        inquiryData,
                        onCancelStepper,
                        type: resType === RESOURCE_TYPE.supplier ? ORGANIZATION_TYPE : PEOPLE_TYPE
                    });
                }
            );
        });
    };

    const steps = useMemo(() => {
        let steps = [];
        if (resType === RESOURCE_TYPE.supplier) {
            steps.push({
                label: 'form.resource.type',
                component: ({}) => <ResourceTypeForm isHireResource />
            });
        }

        steps.push(
            {
                label: 'resource.label',
                component: ({}) => <CreateResourceInStepper resType={resType} />
            },
            {
                label: 'production.label',
                component: ({}) => <CreateProductionForm enableConvertingToManaged isInModal={false} />
            },
            {
                label: 'production.avails',
                component: ({}) => (
                    <CreateRequestAvails
                        resType={resType}
                        expandedContainer={isExpandedModal}
                        setIsExpandedModal={setIsExpandedModal}
                    />
                )
            },
            {
                label: 'preview.and.send',
                component: ({}) => <CreateInvitePreviewStep resType={resType} />
            }
        );

        return steps;
    }, [isExpandedModal, resType]);

    return (
        <FormModal
            isOpen
            id={'hiring-res-modal'}
            title={useI18n('form.crew.hire.title.marketplace') + resType}
            hideButtons
            hideDefaultButtons
            onClose={() => onCancelStepper()}
            sx={{
                width: '100%',
                maxWidth:
                    (resType === RESOURCE_TYPE.supplier ? activeStep === 3 : activeStep === 2) && isExpandedModal
                        ? '1200px'
                        : '600px'
            }}
        >
            <Stepper steps={steps} onSave={onSave} />
        </FormModal>
    );
};
